import {
  Calendar,
  FileText,
  FlaskConical,
  Pill,
  ShieldPlus,
  Syringe,
  Ticket,
  Umbrella,
  type LucideIcon,
} from "lucide-react";
import React from "react";
import type { CardDTO, WalletItem } from "../../services/api/cardService";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { CardGuilloche } from "./CardChrome";
import {
  CARD_FRAME,
  CARD_SHADOW,
  cardMaterial,
  walletEntryCount,
  walletItemLabel,
} from "./cardStyle";

interface Props {
  cardData: CardDTO | null;
  walletItems: WalletItem[];
}

/** Icons by wallet type; the backend's emoji are not drawn on the card. */
const WALLET_ICONS: Record<string, LucideIcon> = {
  emergency: ShieldPlus,
  medications: Pill,
  vaccinations: Syringe,
  checkin: Ticket,
  appointments: Calendar,
  insurance: Umbrella,
  travel_docs: FileText,
  lab_results: FlaskConical,
};

/** Short labels that fit half a card's width, by wallet type. */
const SHORT_LABEL_KEYS: Record<string, keyof Translation> = {
  emergency: "cardxShortEmergency",
  medications: "cardxShortMedication",
  vaccinations: "cardxShortVaccinations",
  checkin: "cardxShortCheckin",
  appointments: "cardxShortAppointments",
  insurance: "cardxShortInsurance",
  travel_docs: "cardxShortTravelDocs",
  lab_results: "cardxShortLabResults",
};

function shortLabel(item: WalletItem, t: Translation): string {
  const key = SHORT_LABEL_KEYS[item.type];
  const value = key ? t[key] : undefined;
  return typeof value === "string" && value ? value : walletItemLabel(item, t);
}

/** The status in words, never colour alone (UX_CONCEPT.md §9.5). */
function walletStatusText(item: WalletItem, t: Translation): string {
  if (item.status === "locked") return item.requiredTier;
  if (item.status === "empty") return t.cardxStatusAdd;
  if (item.count !== null) return walletEntryCount(item.count, t);
  return item.status === "complete"
    ? t.cardxStatusComplete
    : t.cardxStatusPartial;
}

const CardSkeleton: React.FC = () => (
  <div className="w-full max-w-[342px] aspect-[85.6/54] rounded-[18px] bg-primary-100 animate-pulse" />
);

export const HealthPassCardBack: React.FC<Props> = ({
  cardData,
  walletItems,
}) => {
  const { t } = useI18nStore();
  if (!cardData) return <CardSkeleton />;

  const material = cardMaterial(cardData.subscriptionTier);
  const filled = walletItems.filter(
    (item) => item.status === "complete" || item.status === "partial",
  ).length;

  return (
    <div
      className={CARD_FRAME}
      style={{ background: material.background, boxShadow: CARD_SHADOW }}
      aria-label={t.cardxBackAria}
      role="group"
    >
      <CardGuilloche color={material.guilloche} />

      {/* Magnetic stripe */}
      <div
        className="absolute inset-x-0 top-3.5 h-[26px]"
        style={{ background: material.stripe }}
      />

      <div className="absolute left-4 right-4 top-[48px] flex items-baseline justify-between">
        <span className="font-display text-xs font-bold tracking-[0.12em]">
          {t.cardxWalletHeading}
        </span>
        <span className="text-[10px] text-white/75">
          {t.cardxPassesFilled
            .replace("{filled}", String(filled))
            .replace("{total}", String(walletItems.length))}
        </span>
      </div>

      <ul className="absolute left-4 right-4 top-[68px] grid grid-cols-2 gap-x-3.5">
        {walletItems.map((item) => {
          const Icon = WALLET_ICONS[item.type] ?? FileText;
          const done = item.status === "complete" || item.status === "partial";
          const color =
            item.status === "locked"
              ? "rgba(255,255,255,0.6)"
              : done
                ? material.ok
                : material.pending;
          return (
            <li
              key={item.id}
              className="flex h-[25px] items-center gap-1.5 border-b border-white/10"
            >
              <Icon
                className="h-3 w-3 flex-shrink-0 opacity-85"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate text-[10.5px]">
                {shortLabel(item, t)}
              </span>
              <span
                className="whitespace-nowrap text-[9.5px] font-bold"
                style={{ color }}
              >
                {walletStatusText(item, t)}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="absolute bottom-2.5 left-4 right-4 truncate font-mono text-[9px] tracking-[0.04em] text-white/70">
        travelhealthpass.app/card/{cardData.cardNumber}
      </div>
    </div>
  );
};
